/**
 * OnboardingWizard Component
 *
 * Step-by-step first-run experience for new users who have zero specs.
 * Guides users through importing existing specs, connecting repos,
 * and getting started with spec-driven development.
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState, useCallback, useRef } from 'react';
import { Button, Textarea, Badge, Progress, Alert } from 'flowbite-react';
import { importSpecifications, type ImportSpecItem } from '../api/specifications';
import { isSuccessResponse } from '../api/apiClient';

interface OnboardingWizardProps {
  brandName: string;
  brandColor: string;
  onComplete: () => void;
  clientId: string;
}

type WizardStep = 'welcome' | 'import' | 'repository' | 'complete';

interface ParsedSpec {
  title: string;
  content_markdown: string;
  selected: boolean;
}

/**
 * Parse markdown content into individual specs by splitting on H1/H2 headings.
 * Each heading becomes a spec title, content below becomes content_markdown.
 */
function parseMarkdownToSpecs(content: string): ParsedSpec[] {
  const lines = content.split('\n');
  const specs: ParsedSpec[] = [];
  let currentTitle = '';
  let currentContent: string[] = [];

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,2})\s+(.+)$/);

    if (headingMatch) {
      // Save previous spec if exists
      if (currentTitle) {
        specs.push({
          title: currentTitle,
          content_markdown: currentContent.join('\n').trim(),
          selected: true,
        });
      }
      currentTitle = headingMatch[2].trim();
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  }

  // Save last spec
  if (currentTitle) {
    specs.push({
      title: currentTitle,
      content_markdown: currentContent.join('\n').trim(),
      selected: true,
    });
  }

  // If no headings found, treat entire content as one spec
  if (specs.length === 0 && content.trim().length > 0) {
    const firstLine = content.trim().split('\n')[0].slice(0, 80);
    specs.push({
      title: firstLine || 'Imported Spec',
      content_markdown: content.trim(),
      selected: true,
    });
  }

  return specs;
}

/**
 * Read a File as text (supports .md, .txt)
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsText(file);
  });
}

const ACCEPTED_EXTENSIONS = ['.md', '.txt', '.markdown'];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  brandName,
  brandColor,
  onComplete,
  clientId,
}) => {
  const [step, setStep] = useState<WizardStep>('welcome');
  const [parsedSpecs, setParsedSpecs] = useState<ParsedSpec[]>([]);
  const [pasteContent, setPasteContent] = useState('');
  const [importMode, setImportMode] = useState<'file' | 'paste' | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    const allSpecs: ParsedSpec[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();

      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        setError(`Unsupported file type: ${file.name}. Please use .md or .txt files.`);
        continue;
      }

      try {
        const text = await readFileAsText(file);
        const specs = parseMarkdownToSpecs(text);
        allSpecs.push(...specs);
      } catch (err) {
        setError(`Failed to read ${file.name}`);
      }
    }

    if (allSpecs.length > 0) {
      setParsedSpecs(allSpecs);
      setImportMode('file');
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handlePastePreview = useCallback(() => {
    if (!pasteContent.trim()) return;
    const specs = parseMarkdownToSpecs(pasteContent);
    setParsedSpecs(specs);
    setImportMode('paste');
  }, [pasteContent]);

  const toggleSpec = useCallback((index: number) => {
    setParsedSpecs((prev) =>
      prev.map((spec, i) => (i === index ? { ...spec, selected: !spec.selected } : spec))
    );
  }, []);

  const selectedCount = parsedSpecs.filter((s) => s.selected).length;

  const handleImport = useCallback(async () => {
    const specsToImport = parsedSpecs.filter((s) => s.selected);
    if (specsToImport.length === 0) return;

    setImporting(true);
    setError(null);

    try {
      const importData: ImportSpecItem[] = specsToImport.map((s) => ({
        title: s.title,
        content_markdown: s.content_markdown,
        tags: ['imported'],
      }));

      const response = await importSpecifications({
        Client_ID: clientId,
        specs: importData,
      });

      if (isSuccessResponse(response)) {
        const count = response.data?.Records?.length || 0;
        setImportResult({ success: count, total: specsToImport.length });
        // Advance to next step after short delay
        setTimeout(() => setStep('repository'), 1500);
      } else {
        setError(response.message || 'Import failed. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  }, [parsedSpecs, clientId]);

  const stepIndex = { welcome: 0, import: 1, repository: 2, complete: 3 };
  const progressPercent = ((stepIndex[step] + 1) / 4) * 100;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <Progress progress={progressPercent} color="purple" size="sm" />
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span className={step === 'welcome' ? 'font-semibold text-gray-900' : ''}>Welcome</span>
            <span className={step === 'import' ? 'font-semibold text-gray-900' : ''}>Import</span>
            <span className={step === 'repository' ? 'font-semibold text-gray-900' : ''}>
              Repository
            </span>
            <span className={step === 'complete' ? 'font-semibold text-gray-900' : ''}>Done</span>
          </div>
        </div>

        {/* Step 1: Welcome */}
        {step === 'welcome' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: brandColor + '20' }}
            >
              <svg
                className="w-8 h-8"
                style={{ color: brandColor }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold mb-3" style={{ color: brandColor }}>
              Welcome to {brandName}!
            </h1>
            <p className="text-gray-600 text-lg mb-2">
              Spec-driven development for your team.
            </p>
            <p className="text-gray-500 mb-8">
              Keep your specifications, code, and documentation in sync. Let us help you get set up
              in 3 easy steps.
            </p>
            <Button
              size="lg"
              style={{ backgroundColor: brandColor }}
              onClick={() => setStep('import')}
            >
              Get Started
            </Button>
          </div>
        )}

        {/* Step 2: Import Specs */}
        {step === 'import' && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Your Specs</h2>
            <p className="text-gray-500 mb-6">
              Bring in existing specs or documentation. We will parse headings into individual
              specifications.
            </p>

            {error && (
              <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {importResult && (
              <Alert color="success" className="mb-4">
                Successfully imported {importResult.success} of {importResult.total} specifications!
              </Alert>
            )}

            {/* Import mode not chosen yet or showing options */}
            {parsedSpecs.length === 0 && (
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {/* Option A: Upload a file */}
                <div
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    dragOver ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".md,.txt,.markdown"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />
                  <svg
                    className="w-10 h-10 mx-auto text-gray-400 mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="font-medium text-gray-900">Upload Files</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Drag & drop .md or .txt files
                  </p>
                </div>

                {/* Option B: Paste content */}
                <div
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer border-gray-300 hover:border-gray-400 transition-colors"
                  onClick={() => setImportMode('paste')}
                >
                  <svg
                    className="w-10 h-10 mx-auto text-gray-400 mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p className="font-medium text-gray-900">Paste Content</p>
                  <p className="text-xs text-gray-500 mt-1">Paste markdown or text</p>
                </div>

                {/* Option C: Start from scratch */}
                <div
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer border-gray-300 hover:border-gray-400 transition-colors"
                  onClick={() => setStep('repository')}
                >
                  <svg
                    className="w-10 h-10 mx-auto text-gray-400 mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <p className="font-medium text-gray-900">Start from Scratch</p>
                  <p className="text-xs text-gray-500 mt-1">Create specs manually</p>
                </div>
              </div>
            )}

            {/* Paste area (shown when paste mode selected and no parsed specs yet) */}
            {importMode === 'paste' && parsedSpecs.length === 0 && (
              <div className="mb-6">
                <Textarea
                  placeholder="Paste your markdown or text here. Use # or ## headings to separate specs..."
                  rows={10}
                  value={pasteContent}
                  onChange={(e) => setPasteContent(e.target.value)}
                  className="mb-3"
                />
                <div className="flex gap-3">
                  <Button
                    style={{ backgroundColor: brandColor }}
                    onClick={handlePastePreview}
                    disabled={!pasteContent.trim()}
                  >
                    Preview Specs
                  </Button>
                  <Button
                    color="light"
                    onClick={() => {
                      setImportMode(null);
                      setPasteContent('');
                    }}
                  >
                    Back
                  </Button>
                </div>
              </div>
            )}

            {/* Parsed specs preview */}
            {parsedSpecs.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Parsed Specifications ({parsedSpecs.length})
                  </h3>
                  <Badge color="info">{selectedCount} selected</Badge>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto mb-6">
                  {parsedSpecs.map((spec, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        spec.selected
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                      onClick={() => toggleSpec(index)}
                    >
                      <input
                        type="checkbox"
                        checked={spec.selected}
                        onChange={() => toggleSpec(index)}
                        className="mt-1 rounded"
                        style={{ accentColor: brandColor }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{spec.title}</p>
                        <p className="text-sm text-gray-500 truncate">
                          {spec.content_markdown.slice(0, 120)}
                          {spec.content_markdown.length > 120 ? '...' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button
                    style={{ backgroundColor: brandColor }}
                    onClick={handleImport}
                    disabled={selectedCount === 0 || importing}
                  >
                    {importing
                      ? 'Importing...'
                      : `Import ${selectedCount} Spec${selectedCount !== 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    color="light"
                    onClick={() => {
                      setParsedSpecs([]);
                      setImportMode(null);
                      setPasteContent('');
                    }}
                  >
                    Start Over
                  </Button>
                  <Button color="light" onClick={() => setStep('repository')}>
                    Skip
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Connect Repository */}
        {step === 'repository' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: brandColor + '20' }}
            >
              <svg
                className="w-8 h-8"
                style={{ color: brandColor }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect a Repository</h2>
            <p className="text-gray-500 mb-6">
              Link a GitHub repository to enable code-spec drift detection. {brandName} will
              monitor your code for changes that affect your specifications.
            </p>
            <div className="flex justify-center gap-3">
              <Button
                size="lg"
                style={{ backgroundColor: brandColor }}
                onClick={() => {
                  window.location.href = '/settings/github-app';
                }}
              >
                Connect GitHub
              </Button>
              <Button
                size="lg"
                color="light"
                onClick={() => setStep('complete')}
              >
                Skip for now
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: '#10B98120' }}
            >
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You are All Set!</h2>
            <p className="text-gray-500 mb-6">
              {importResult
                ? `${importResult.success} specs imported.`
                : 'Your workspace is ready.'}{' '}
              You can create specs, connect repositories, and start tracking drift at any time.
            </p>
            <Button
              size="lg"
              style={{ backgroundColor: brandColor }}
              onClick={onComplete}
            >
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
